import { useCallback, useEffect, useRef, useState } from "react";
import { apiUrl } from "../lib/apiBase";
import { openExternalUrl } from "../lib/appMode";

function getToken() {
  try {
    return (
      localStorage.getItem("ga_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("jwt") ||
      sessionStorage.getItem("ga_token") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("authToken") ||
      sessionStorage.getItem("jwt") ||
      ""
    );
  } catch {
    return "";
  }
}

function channelFromPath() {
  try {
    const match = window.location.pathname.match(/^\/stream\/([^/?#]+)/i);
    return match?.[1] ? decodeURIComponent(match[1]).trim().toLowerCase() : "";
  } catch {
    return "";
  }
}

async function readJson(response) {
  return response.json().catch(() => ({}));
}

export default function StreamerCheerOnboardingBridge() {
  const channel = channelFromPath();
  const [selfStatus, setSelfStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const buttonRef = useRef(null);
  const clickHandlerRef = useRef(null);

  const refreshSelfStatus = useCallback(async () => {
    const token = getToken();
    if (!token || !channel) {
      setSelfStatus(null);
      return null;
    }

    try {
      const response = await fetch(
        apiUrl(`/cheers/creator/self-status/${encodeURIComponent(channel)}`),
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        }
      );
      const data = await readJson(response);
      if (!response.ok || !data?.ok) {
        setSelfStatus(null);
        return null;
      }
      setSelfStatus(data);
      return data;
    } catch {
      setSelfStatus(null);
      return null;
    }
  }, [channel]);

  const syncCreator = useCallback(async () => {
    const token = getToken();
    if (!token || !channel) return null;

    const response = await fetch(
      apiUrl(`/cheers/creator/self-sync/${encodeURIComponent(channel)}`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ displayName: channel }),
      }
    );
    const data = await readJson(response);
    if (!response.ok || !data?.ok) {
      throw new Error(data?.error || data?.message || "Unable to prepare GAS Cheers payouts.");
    }
    return data;
  }, [channel]);

  const startOnboarding = useCallback(async () => {
    if (busy || !channel) return;
    const token = getToken();
    if (!token) return;

    setBusy(true);
    try {
      // First reuse/sync any Stripe Connect account the streamer may already
      // have from tournament payout onboarding. No duplicate account needed.
      const sync = await syncCreator();
      if (sync?.payoutReady) {
        window.location.reload();
        return;
      }

      const returnPath = `/stream/${encodeURIComponent(channel)}?cheer_onboard=1`;
      const response = await fetch(apiUrl("/connect/onboard"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ returnPath }),
      });
      const data = await readJson(response);
      if (!response.ok || !data?.ok || !data?.url) {
        throw new Error(data?.message || "Unable to open Stripe payout setup.");
      }

      await openExternalUrl(data.url);
    } catch (error) {
      console.error("[GAS Cheers streamer onboarding]", error);
      window.alert(error?.message || "Unable to start GAS Cheers payout setup right now.");
      setBusy(false);
    }
  }, [busy, channel, syncCreator]);

  useEffect(() => {
    refreshSelfStatus();
  }, [refreshSelfStatus]);

  useEffect(() => {
    if (!channel || !selfStatus?.isStreamerOwner) return undefined;

    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const returning = params.get("cheer_onboard") === "1" && params.get("connect") === "return";
    if (!returning) return undefined;

    (async () => {
      try {
        const sync = await syncCreator();
        if (cancelled) return;
        if (sync?.payoutReady) {
          params.delete("cheer_onboard");
          params.delete("connect");
          const qs = params.toString();
          const cleanUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash || ""}`;
          window.location.replace(cleanUrl);
        } else {
          await refreshSelfStatus();
        }
      } catch (error) {
        console.error("[GAS Cheers streamer return sync]", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [channel, selfStatus?.isStreamerOwner, syncCreator, refreshSelfStatus]);

  useEffect(() => {
    const shouldEnableOnboarding = Boolean(
      selfStatus?.isStreamerOwner && !selfStatus?.payoutReady
    );

    const detach = () => {
      if (buttonRef.current && clickHandlerRef.current) {
        buttonRef.current.removeEventListener("click", clickHandlerRef.current, true);
      }
      buttonRef.current = null;
      clickHandlerRef.current = null;
    };

    if (!shouldEnableOnboarding) {
      detach();
      return undefined;
    }

    const attach = () => {
      const button = document.querySelector(".chatbox-gas-btn");
      if (!button) return;

      // React intentionally disables this button for viewers when the creator
      // has not finished payout setup. The verified channel owner is the one
      // exception: their click starts their own Stripe Connect onboarding.
      button.disabled = false;
      button.removeAttribute("disabled");
      button.title = "Set up your GAS Cheers payouts";
      button.style.cursor = busy ? "wait" : "pointer";
      button.style.opacity = busy ? "0.78" : "1";
      button.style.background =
        "linear-gradient(135deg, rgba(0,180,255,0.24), rgba(236,72,153,0.24))";

      if (buttonRef.current === button && clickHandlerRef.current) return;
      detach();

      const handler = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }
        startOnboarding();
      };

      button.addEventListener("click", handler, true);
      buttonRef.current = button;
      clickHandlerRef.current = handler;
    };

    attach();
    const timer = window.setInterval(attach, 350);

    return () => {
      window.clearInterval(timer);
      detach();
    };
  }, [selfStatus?.isStreamerOwner, selfStatus?.payoutReady, busy, startOnboarding]);

  return null;
}
